import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
// Models
import {
  Pilot,
  User,
  Vehicle,
  Insurance,
  FlightSession,
  MultipleApiResponse
} from '@mocca-technologies/model';
import { droneIcon, droneMoccaIcon } from 'src/assets/icons';
// Services
import { DroneOperatorService } from 'src/app/services/drone-operator.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
import { catchError, firstValueFrom, forkJoin, map, of, Subject, takeUntil } from 'rxjs';
import { LoadingGuardService } from 'src/app/services/loading-guard.service';

@Component({
  selector: 'app-vehicle-info-card',
  templateUrl: './vehicle-info-card.component.html',
  styleUrls: ['./vehicle-info-card.component.scss'],
})
export class VehicleInfoCardComponent implements OnInit, OnDestroy, OnChanges {
  user: User;
  droneIcon = droneIcon;
  droneMoccaIcon = droneMoccaIcon;

  pilotList: Pilot[] = [];

  @Input() vehicle: Vehicle;
  @Input() isSummary: boolean = false;

  /**
   * IMPORTANT: Fleet HTML uses (sessionsUpdated)
   */
  @Output() sessionsUpdated: EventEmitter<void> = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private opChain: Promise<void> = Promise.resolve();

  get isAdminDevel(): boolean {
    return this.user?.profile === 'admin' || this.user?.profile === 'devel';
  }

  constructor(
    private loadingController: LoadingController,
    private cdr: ChangeDetectorRef,
    private droneOperatorService: DroneOperatorService,
    private alertController: AlertController,
    private router: Router,
    private refreshService: RefreshService,
    private httpResponseService: HttpResponsesService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.user = localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user')!)
      : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Auto load pilots for admin/devel when vehicle changes
    if (changes['vehicle'] && this.vehicle?.uniqueId && this.isAdminDevel && !this.isSummary) {
      this.loadPilots();
    }

    if (changes['vehicle'] && this.vehicle?.uniqueId && !this.isAdminDevel) {
      this.pilotList = [];
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** @returns true if insurance has no data */
  noInsuranceInfo(insurance: Insurance): boolean {
    return (
      !insurance ||
      (!insurance?.insuranceNumber &&
        !insurance?.expirationDate &&
        !insurance?.insuranceIssuer &&
        !insurance?.country)
    );
  }

  /** @returns true if insurance exists but is incomplete */
  noCompleteInsuranceInfo(insurance: Insurance): boolean {
    return (
      !this.noInsuranceInfo(insurance) &&
      (!insurance ||
        !insurance?.insuranceNumber ||
        !insurance?.expirationDate ||
        !insurance?.insuranceIssuer ||
        !insurance?.country)
    );
  }

  /**
   * Deletes session pilot<->vehicle.
   */
  async deleteSession(pilot: Pilot): Promise<void> {
    const confirmAlert = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.DELETE_SESSION_HEADER'),
      message: this.translateService.instant('PAGES.FLEET.DELETE_SESSION_MESSAGE', {
        vehicle: this.vehicle?.plate,
        pilot: pilot.contactInformation?.name || pilot.uniqueId,
      }),
      buttons: [
        { text: this.translateService.instant('COMMON.BUTTONS.CANCEL'), role: 'cancel', cssClass: 'secondary' },
        {
          text: this.translateService.instant('COMMON.BUTTONS.CONFIRM'),
          handler: () => {
            this.droneOperatorService
              .deleteSession(pilot.uniqueId, this.vehicle?.uniqueId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (response: any) => {
                  this.httpResponseService.showServerError(response?.status, response?.body?.responseMessage);
                  if (response?.status === 200) {
                    this.loadPilots();
                    this.sessionsUpdated.emit();
                  }
                },
                error: (error: HttpErrorResponse) =>
                  this.httpResponseService.showServerError(error?.status, error?.error),
              });
          },
        },
      ],
    });

    await confirmAlert.present();
  }

  /**
   * Navigate to vehicle edit form.
   */
  goToVehicleForm(): void {
    this.router.navigate(['/vehicle-form'], { state: { vehicle: this.vehicle, isEdit: true } });
  }

  /**
   * Deletes a vehicle.
   */
  async deleteVehicle(vehicle: Vehicle): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.DELETE_VEHICLE_HEADER', { vehicle: vehicle.plate }),
      message: this.translateService.instant('PAGES.FLEET.DELETE_VEHICLE_MESSAGE', { vehicle: vehicle.plate }),
      buttons: [
        { text: this.translateService.instant('COMMON.BUTTONS.CANCEL'), role: 'cancel' },
        {
          text: this.translateService.instant('COMMON.BUTTONS.YES'),
          handler: () => {
            this.droneOperatorService
              .deleteVehicle(vehicle.uniqueId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (response: any) => {
                  this.httpResponseService.showServerError(response?.status, response?.body?.responseMessage);
                  if (response?.status === 200) this.refreshService.triggerRefresh('fleet');
                },
                error: (error: HttpErrorResponse) =>
                  this.httpResponseService.showServerError(error?.status, error?.error),
              });
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * Loads pilots associated to the vehicle (admin/devel).
   * Uses internal queue to prevent loader overlap.
   */
  async loadPilots(): Promise<void> {
    return this.enqueue(async () => {
      if (!this.isAdminDevel || !this.vehicle?.uniqueId) {
        this.pilotList = [];
        this.cdr.detectChanges();
        return;
      }

      const loading = await this.loadingController.create({
        message: this.translateService.instant('COMMON.LOADING.PILOTS'),
        spinner: 'bubbles',
      });
      await loading.present();

      try {
        this.pilotList = [];

        const sessionsRes = await firstValueFrom(
          this.droneOperatorService.getVehicleSessions(this.vehicle.uniqueId).pipe(takeUntil(this.destroy$)),
        );

        const sessions = (sessionsRes?.body?.responseObject || []) as FlightSession[];
        const userUuids = Array.from(new Set((sessions || []).map((s) => s?.userUuid).filter(Boolean)));

        if (userUuids.length === 0) {
          this.pilotList = [];
          this.cdr.detectChanges();
          return;
        }

        const reqs = userUuids.map((uuid) =>
          this.droneOperatorService.getPilot(uuid).pipe(
            takeUntil(this.destroy$),
            map((r: HttpResponse<MultipleApiResponse>) => r?.body?.responseObject as Pilot),
            catchError(() => of(null)),
          ),
        );

        const pilots = await firstValueFrom(
          forkJoin(reqs).pipe(
            map((list) =>
              (list || [])
                .filter((p) => !!p && !!p.uniqueId)
                .filter((p) => p.organizationUuid === this.vehicle?.organizationUuid)
                .filter((p, i, arr) => arr.findIndex((x) => x.uniqueId === p.uniqueId) === i),
            ),
          ),
        );

        this.pilotList = pilots || [];
        this.cdr.detectChanges();
      } catch (error: any) {
        this.httpResponseService.showServerError(error?.status, error?.error);
      } finally {
        await this.safeDismissLoading(loading);
      }
    });
  }

  private enqueue(op: () => Promise<void>): Promise<void> {
    this.opChain = this.opChain.then(async () => {
      if (this.destroy$.closed) return;
      await op();
    }).catch(() => {});
    return this.opChain;
  }

    // #region --> Drop and Drag Methods

  async onPilotDropped(event: CdkDragDrop<Pilot[]>) {
    if (event.previousContainer === event.container) return;

    const sourcePilot = event.item.data;
    if (!sourcePilot) return;

    const alreadyExists = this.pilotList.some(
      (p) => p.uniqueId === sourcePilot.uniqueId,
    );

    if (alreadyExists) {
      const alert = await this.alertController.create({
        header: this.translateService.instant('PAGES.FLEET.DUPLICATE_PILOT_HEADER'),
        message: this.translateService.instant('PAGES.FLEET.DUPLICATE_PILOT_MESSAGE'),
        buttons: [this.translateService.instant('COMMON.BUTTONS.OK')],
      });
      await alert.present();
      return;
    }

    const pilotName = sourcePilot.contactInformation?.name || sourcePilot.uniqueId;

    const confirmAlert = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.CONFIRM_PILOT_HEADER'),
      message: this.translateService.instant('PAGES.FLEET.CONFIRM_PILOT_MESSAGE', {
        pilot: pilotName,
        vehicle: this.vehicle.plate,
      }),
      buttons: [
        {
          text: this.translateService.instant('COMMON.BUTTONS.CANCEL'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translateService.instant('COMMON.BUTTONS.CONFIRM'),
          handler: () => {
            const pilotCopy: Pilot = { ...sourcePilot };

            this.droneOperatorService
              .createSession(pilotCopy?.uniqueId, this.vehicle?.uniqueId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: async (response: HttpResponse<MultipleApiResponse>) => {
                  this.httpResponseService.showServerError(
                    response?.status,
                    response?.body?.responseMessage,
                  );

                  if (response?.status === 200) {
                    this.loadPilots();
                    this.sessionsUpdated.emit();
                  }
                },
                error: async (error: HttpErrorResponse) => {
                  this.httpResponseService.showServerError(
                    error?.status,
                    error?.error,
                  );
                },
              });
          },
        },
      ],
    });

    await confirmAlert.present();
  }

  // #endregion --> Drop and Drag Methods


  private async safeDismissLoading(loading?: HTMLIonLoadingElement): Promise<void> {
    if (!loading) return;
    try { await loading.dismiss(); } catch {}
  }
}
